/**
 * Diamond Pairs — the brain rules (specs/pairs-mode.md §5.2, §5.3).
 *
 * Kept out of PairsScene on purpose: this is the mode's ONE mechanic, and Phaser cannot be
 * imported in the node test environment (see vitest.config.ts), so anything left inside the scene
 * is untestable. src/game/pairs/rules.test.ts pins every case without booting a game.
 */

/**
 * Does this mismatch cost a brain?
 *
 * Only when the player had already seen BOTH cards before this turn — i.e. a brain is charged for
 * *forgetting*, never for *not yet knowing*. Turning a card that has never been face-up cannot be
 * a mistake, whatever the other card is: the information needed to avoid it did not exist.
 *
 * The stricter reading (charge whenever EITHER card is on its second look) was rejected because it
 * punishes probing an unknown card with a known one — which is the correct way to play a pairs
 * game, and the only way to make progress early in a round.
 *
 * `seenA`/`seenB` are the flags as they stood BEFORE the two cards were turned.
 */
export function mismatchCostsBrain(seenA: boolean, seenB: boolean): boolean {
  return seenA && seenB;
}

/**
 * Brains after a cleared round: a round finished without losing one gives a brain back, capped at
 * the starting count. Without the refund a run is a one-way slide and the ladder past round 4 is
 * unreachable; with it, a perfect 24-card board buys back the mistake made two rounds ago.
 */
export function brainsAfterRound(brains: number, lostThisRound: number, max: number): number {
  if (lostThisRound > 0) return brains;
  return Math.min(max, brains + 1);
}
